import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  CheckSquare, 
  FileText,
  Settings,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Workflow,
  MessageCircle,
  UsersRound,
  Bell,
  Receipt,
  LogOut
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useCompany } from '../../contexts/CompanyContext';
import { useAuth } from '../../contexts/AuthContext';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Notifications', href: '/notifications', icon: Bell },
  { name: 'Clients', href: '/clients', icon: Users },
  { 
    name: 'Workflow', 
    icon: Workflow,
    subItems: [
      { name: 'Tasks', href: '/tasks', icon: CheckSquare },
      { name: 'Pipelines', href: '/pipelines', icon: Workflow }
    ]
  },
  { name: 'Invoices', href: '/invoices', icon: Receipt },
  { name: 'Documents', href: '/documents', icon: FileText },
  { name: 'Team Chat', href: '/team-chat', icon: MessageCircle },
  { name: 'Team', href: '/team', icon: UsersRound },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [workflowOpen, setWorkflowOpen] = useState(true);
  const { settings } = useCompany();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen w-64 flex-col fixed left-0 top-0 border-r bg-white">
      {/* Logo */}
      <div className="flex h-16 items-center border-b px-4">
        <div className="flex items-center gap-2 min-w-0">
          {settings?.logo ? (
            <img 
              src={settings.logo} 
              alt="Company Logo" 
              className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">MC</span>
            </div>
          )}
          <span className="text-sm font-bold text-gray-900 leading-tight line-clamp-2">{settings?.name || 'Mantra CRM'}</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        <ul className="space-y-1">
          {navigation.map((item) => {
            if (item.subItems) {
              const isAnySubItemActive = item.subItems.some(
                subItem => location.pathname === subItem.href
              );
              return (
                <li key={item.name}>
                  <button
                    onClick={() => setWorkflowOpen(!workflowOpen)}
                    className={cn(
                      'flex items-center justify-between w-full rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      isAnySubItemActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="h-5 w-5" />
                      {item.name}
                    </div>
                    {workflowOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {workflowOpen && (
                    <ul className="mt-1 ml-6 space-y-1">
                      {item.subItems.map((subItem) => {
                        const isActive = location.pathname === subItem.href;
                        return (
                          <li key={subItem.name}>
                            <Link
                              to={subItem.href}
                              className={cn(
                                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                isActive
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                              )}
                            >
                              <subItem.icon className="h-4 w-4" />
                              {subItem.name}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            }
            
            const isActive = location.pathname === item.href;
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Profile */}
      <div className="border-t p-4">
        <div className="flex items-center gap-3">
          {user?.avatar ? (
            <img
              src={user.avatar}
              alt="User"
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold">
              {user?.firstName?.charAt(0) || user?.name?.charAt(0) || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}` 
                : user?.name || 'User'}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.role || 'User'}</p>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
